import {
  ActiveSubscription,
  BigDecimalValue,
  CwayModule,
  OfferDiscount,
  SubscriptionDiscount,
  SubscriptionOffer,
  SubscriptionOfferLine,
  SubscriptionOfferStatus,
  SubscriptionProduct,
  SubscriptionTier,
} from '../types/graphql';
import type {PillTone} from '../components/SubscriptionBadges';
import {DEFAULT_SEAT_TYPE, formatDate, formatMoney, isCwayTrial, isSeatProduct, MODULE_LABELS, toNumber, UNLIMITED} from './subscriptions';

/** A discount of this size costs the customer nothing at all. */
export const FREE_PERCENT = 100;

/** The server's bounds on the trial an offer may carry. */
export const MIN_TRIAL_DAYS = 1;
export const MAX_TRIAL_DAYS = 730;

/** The server's bounds on the payment terms an invoiced offer may carry (net 1 to net 90). */
export const MIN_PAYMENT_TERMS_DAYS = 1;
export const MAX_PAYMENT_TERMS_DAYS = 90;

/** The terms sales reaches for unless the deal says otherwise. */
export const SUGGESTED_PAYMENT_TERMS_DAYS = 30;

export const OFFER_STATUS_TONES: Record<SubscriptionOfferStatus, PillTone> = {
  OPEN: 'blue',
  ACCEPTED: 'green',
  REVOKED: 'red',
  EXPIRED: 'gray',
};

export const OFFER_STATUS_LABELS: Record<SubscriptionOfferStatus, string> = {
  OPEN: 'open',
  ACCEPTED: 'accepted',
  REVOKED: 'revoked',
  EXPIRED: 'expired',
};

/** Percentages arrive as BigDecimal strings; render 25 as "25" and 12.50 as "12.5". */
export function formatPercent(value: BigDecimalValue | null | undefined): string {
  const numeric = toNumber(value);
  if (numeric === null) return '—';
  return `${Number(numeric.toFixed(2))}%`;
}

/**
 * Prose for a discount as sales and the customer read it: "Free", "Free for 3 months",
 * "25% off", "25% off for 12 months". Null when the line is billed at list price.
 */
export function describeOfferDiscount(discount: OfferDiscount | null | undefined): string | null {
  if (!discount) return null;
  const percent = toNumber(discount.percentOff);
  if (percent === null) return null;
  const head = percent >= FREE_PERCENT ? 'Free' : `${formatPercent(percent)} off`;
  const months = discount.durationMonths ?? null;
  if (!months) return head;
  return `${head} for ${months} ${months === 1 ? 'month' : 'months'}`;
}

/**
 * The same prose for a discount on a live subscription, where Stripe reports an end date
 * instead of a month count.
 */
export function describeSubscriptionDiscount(discount: SubscriptionDiscount | null | undefined): string | null {
  if (!discount) return null;
  const percent = toNumber(discount.percentOff);
  if (percent === null) return null;
  const head = percent >= FREE_PERCENT ? 'Free' : `${formatPercent(percent)} off`;
  return discount.endsAt ? `${head} until ${formatDate(discount.endsAt)}` : head;
}

/** `amount` with `percentOff` taken off it. */
export function applyDiscount(amount: number, percentOff: BigDecimalValue | null | undefined): number {
  const percent = toNumber(percentOff);
  if (percent === null) return amount;
  return amount * (1 - percent / 100);
}

/** Monthly list amount for one offer line: unit price × quantity. Null when the product is gone. */
export function lineListAmount(line: SubscriptionOfferLine): number | null {
  const amount = toNumber(line.product?.price?.amount);
  if (amount === null) return null;
  return amount * (toNumber(line.quantity) ?? 1);
}

export interface OfferTotals {
  currency: string;
  /** What the same basket costs without the deal. */
  list: number;
  /** What the customer pays while every discount is still running. */
  discounted: number;
  /** True when a line's product could not be resolved, so the totals are incomplete. */
  incomplete: boolean;
}

/**
 * Monthly totals per currency while the deal is at its deepest — line discounts first, then the
 * global one on top, which is the order Stripe applies them in. Time-limited discounts fall away
 * later, so this is the first-month price, not the lifetime one.
 */
export function offerTotals(offer: Pick<SubscriptionOffer, 'lines' | 'globalDiscount'>): OfferTotals[] {
  const byCurrency = new Map<string, OfferTotals>();
  let incomplete = false;
  for (const line of offer.lines) {
    const amount = lineListAmount(line);
    const currency = (line.product?.price?.currency || '').toUpperCase();
    if (amount === null || !currency) {
      // An archived product has no price to add; say so rather than under-reporting the total.
      incomplete = true;
      continue;
    }
    const totals = byCurrency.get(currency) || {currency, list: 0, discounted: 0, incomplete: false};
    totals.list += amount;
    totals.discounted += applyDiscount(amount, line.discount?.percentOff);
    byCurrency.set(currency, totals);
  }
  return [...byCurrency.values()].map((row) => ({
    ...row,
    discounted: applyDiscount(row.discounted, offer.globalDiscount?.percentOff),
    incomplete,
  }));
}

export function formatOfferTotals(totals: OfferTotals[]): string {
  if (totals.length === 0) return '—';
  return totals.map((row) => formatMoney(row.discounted, row.currency)).join(' + ');
}

/**
 * The subscription an offer changes on acceptance: the active one, unless it is a CWAY-managed
 * trial, which the server treats as "no subscription" (the offer then creates one). A Stripe
 * trial, a past-due or an unpaid subscription all count — they are still the billing relationship.
 */
export type OfferTarget = Pick<ActiveSubscription, 'id' | 'autoRenewal' | 'products' | 'discount' | 'paymentTermsDays'>;

export function offerTargetOf<T extends Pick<ActiveSubscription, 'id' | 'isActive' | 'isCwayTrial'>>(subscription: T | null | undefined): T | null {
  if (!subscription || !subscription.isActive || isCwayTrial(subscription)) return null;
  return subscription;
}

/**
 * Whole months from `now` until `endsAt`, rounded up and never below one. A Stripe coupon that
 * runs for N months ends N calendar months after it started, so the months left on it are what
 * an offer has to say to carry it over unchanged.
 */
export function remainingMonths(endsAt: number, now = Date.now()): number {
  const start = new Date(now);
  const end = new Date(endsAt);
  let months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
  const anchor = new Date(start);
  anchor.setUTCMonth(start.getUTCMonth() + months);
  if (anchor.getTime() < end.getTime()) months += 1;
  return Math.max(1, months);
}

/** A live discount restated as offer terms: the same percentage for the months it has left. */
export function offerDiscountFromSubscription(discount: SubscriptionDiscount | null | undefined, now = Date.now()): OfferDiscount | null {
  if (!discount) return null;
  return {percentOff: discount.percentOff, durationMonths: discount.endsAt ? remainingMonths(discount.endsAt, now) : null};
}

export interface OfferLinePrefill {
  productId: string;
  quantity: number;
  discount: OfferDiscount | null;
}

export interface OfferPrefill {
  lines: OfferLinePrefill[];
  globalDiscount: OfferDiscount | null;
}

/**
 * The builder's starting point for an offer on a running subscription: every current line with
 * its quantity and discount, and the subscription-level discount, so what sales submits is the
 * whole new subscription and dropping a line or a discount is a deliberate act.
 */
export function prefillFromSubscription(subscription: Pick<ActiveSubscription, 'products' | 'discount'>, now = Date.now()): OfferPrefill {
  return {
    lines: subscription.products.map((product) => ({
      productId: product.id,
      quantity: toNumber(product.quantity) ?? 1,
      discount: offerDiscountFromSubscription(product.discount, now),
    })),
    globalDiscount: offerDiscountFromSubscription(subscription.discount, now),
  };
}

/**
 * Seats a catalog product includes, from its Stripe metadata (the same key the model reads).
 * `UNLIMITED` for a tier that seats everyone; null when the product says nothing about seats.
 */
export function includedSeatsOf(product: Pick<SubscriptionProduct, 'metadata'> | null | undefined): number | null {
  const raw = product?.metadata?.find((entry) => entry.key === 'included-users')?.value.trim();
  if (!raw) return null;
  if (raw.toLowerCase() === 'unlimited') return UNLIMITED;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

/**
 * Seats the organisation will have once the offer is accepted: the seat line's quantity plus
 * what the offered Project tier includes. `UNLIMITED` when that tier seats everyone. No seat
 * line means no purchased seats — the offer is the whole subscription.
 */
export function offeredSeats(lines: SubscriptionOfferLine[]): number {
  const projectTier = lines.find((line) => line.product?.module === 'PROJECT' && !line.product.addon);
  const included = includedSeatsOf(projectTier?.product) ?? 0;
  if (included === UNLIMITED) return UNLIMITED;
  // Only the project seat line: a Lightbox seat line on the same offer is a separate block.
  const seatLine = lines.find((line) => line.product?.seatType === DEFAULT_SEAT_TYPE);
  const purchased = seatLine ? (toNumber(seatLine.quantity) ?? 1) : 0;
  return included + purchased;
}

/** Members who lose their seat on acceptance: the server trims to the offered count and mails the owners. */
export function seatShortfall(lines: SubscriptionOfferLine[], usedSeats: number): number {
  const offered = offeredSeats(lines);
  if (offered === UNLIMITED) return 0;
  return Math.max(0, usedSeats - offered);
}

export interface OfferProductGroup {
  key: string;
  label: string;
  products: SubscriptionProduct[];
}

const MODULE_ORDER = Object.keys(MODULE_LABELS) as CwayModule[];
const TIER_ORDER: SubscriptionTier[] = ['STARTER', 'PRO', 'ENTERPRISE'];

/** Words that say nothing about what a product is, so two names sharing one are not a family. */
const NAME_NOISE = new Set(['cway', 'the', 'and', 'for', 'of', 'plan', 'add', 'on', ...TIER_ORDER.map((tier) => tier.toLowerCase())]);

function nameWords(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** True when `words` contains `phrase` as consecutive words: "Brand Studio onboarding" has "brand studio". */
function hasPhrase(words: string[], phrase: string[]): boolean {
  return words.some((_, start) => phrase.every((word, offset) => words[start + offset] === word));
}

/** Base tiers in price order, then the overage units, then anything else by name. */
function moduleRank(product: SubscriptionProduct): number {
  if (product.tier && !product.addon) return TIER_ORDER.indexOf(product.tier);
  return product.addon ? TIER_ORDER.length : TIER_ORDER.length + 1;
}

const byName = (a: SubscriptionProduct, b: SubscriptionProduct) => a.name.localeCompare(b.name);

/**
 * The offer picker's sections. Typed fields decide first — a module's tiers and overage units,
 * then seats — because names in Stripe are free text. Everything left is grouped by its name: a
 * product naming a module ("Project onboarding") joins that module, products sharing a word
 * ("Enhanced Support", "Premium Support") form a family, and the singletons end up in "Other".
 */
export function groupOfferProducts(products: SubscriptionProduct[]): OfferProductGroup[] {
  const modules = new Map<CwayModule, SubscriptionProduct[]>();
  const seats: SubscriptionProduct[] = [];
  const untyped: SubscriptionProduct[] = [];
  const addToModule = (module: CwayModule, product: SubscriptionProduct) => modules.set(module, [...(modules.get(module) ?? []), product]);

  for (const product of products) {
    if (product.module) addToModule(product.module, product);
    else if (isSeatProduct(product)) seats.push(product);
    else {
      const words = nameWords(product.name);
      const named = MODULE_ORDER.find((module) => hasPhrase(words, nameWords(MODULE_LABELS[module])));
      if (named) addToModule(named, product);
      else untyped.push(product);
    }
  }

  const significant = (product: SubscriptionProduct) => [...new Set(nameWords(product.name).filter((word) => !NAME_NOISE.has(word)))];
  const wordCounts = new Map<string, number>();
  for (const product of untyped) significant(product).forEach((word) => wordCounts.set(word, (wordCounts.get(word) ?? 0) + 1));

  const families = new Map<string, OfferProductGroup>();
  const other: SubscriptionProduct[] = [];
  for (const product of untyped) {
    // The most widely shared word wins; on a tie, the one that comes first in the name.
    const family = significant(product)
      .filter((word) => (wordCounts.get(word) ?? 0) > 1)
      .reduce<string | null>((best, word) => (best === null || wordCounts.get(word)! > wordCounts.get(best)! ? word : best), null);
    if (!family) {
      other.push(product);
      continue;
    }
    const group = families.get(family) ?? {key: `name:${family}`, label: family.charAt(0).toUpperCase() + family.slice(1), products: []};
    group.products.push(product);
    families.set(family, group);
  }

  return [
    ...MODULE_ORDER.filter((module) => modules.has(module)).map((module) => ({
      key: `module:${module}`,
      label: MODULE_LABELS[module],
      products: modules.get(module)!.sort((a, b) => moduleRank(a) - moduleRank(b) || byName(a, b)),
    })),
    ...(seats.length > 0 ? [{key: 'seats', label: 'Seats', products: seats.sort(byName)}] : []),
    ...[...families.values()].sort((a, b) => a.label.localeCompare(b.label)).map((group) => ({...group, products: group.products.sort(byName)})),
    ...(other.length > 0 ? [{key: 'other', label: 'Other', products: other.sort(byName)}] : []),
  ];
}
